import { useState, useEffect, useCallback, useRef } from 'react';
import { IdentityService } from '../fetcher/IdentityService.js';

/**
 * @param {string} tableId
 * @param {import('./Column.js').Column[]} columns
 * @param {string[]} [defaultHiddenKeys=[]] - Column keys hidden when user has never saved settings (or legacy payload without `hidden`).
 */
export const useColumnSettings = (tableId, columns, defaultHiddenKeys = []) => {
  const defaultHiddenRef = useRef(defaultHiddenKeys);
  defaultHiddenRef.current = defaultHiddenKeys;

  const [state, setState] = useState({
    columnOrder: [],
    hiddenColumns: [],
    isLoading: true
  });
  
  const identityServiceRef = useRef(null);
  if (!identityServiceRef.current) {
    identityServiceRef.current = new IdentityService();
  }
  
  const settingsKey = `columns_${tableId}`;
  const initializedRef = useRef(false);

  // Fetch on mount
  useEffect(() => {
    if (initializedRef.current || !columns?.length) return;
    initializedRef.current = true;

    const defaultOrder = columns.map(c => c.key);
    const fallbackHidden = () => [...defaultHiddenRef.current];

    identityServiceRef.current.fetchUserSettings()
      .then(allSettings => {
        const json = allSettings[settingsKey];
        if (json) {
          try {
            const parsed = JSON.parse(json);
            const savedOrder = Array.isArray(parsed.order) ? parsed.order : defaultOrder;
            // Keep saved order, then append any newly introduced columns
            const columnOrder = [
              ...savedOrder.filter(key => defaultOrder.includes(key)),
              ...defaultOrder.filter(key => !savedOrder.includes(key))
            ];
            const savedHidden = Array.isArray(parsed.hidden)
              ? parsed.hidden.filter(key => defaultOrder.includes(key))
              : fallbackHidden();
            // Newly added default-hidden columns stay hidden until the user opts in
            const knownKeys = new Set([...savedOrder, ...savedHidden]);
            const newlyDefaultHidden = defaultHiddenRef.current.filter(
              key => defaultOrder.includes(key) && !knownKeys.has(key)
            );
            setState({
              columnOrder,
              hiddenColumns: [...savedHidden, ...newlyDefaultHidden],
              isLoading: false
            });
          } catch {
            setState({
              columnOrder: defaultOrder,
              hiddenColumns: fallbackHidden(),
              isLoading: false
            });
          }
        } else {
          setState({
            columnOrder: defaultOrder,
            hiddenColumns: fallbackHidden(),
            isLoading: false
          });
        }
      })
      .catch(err => {
        console.error('Failed to fetch settings:', err);
        setState({
          columnOrder: defaultOrder,
          hiddenColumns: fallbackHidden(),
          isLoading: false
        });
      });
  }, [columns, settingsKey]);

  // Save function - updates state AND persists
  const saveSettings = useCallback((newOrder, newHidden) => {
    setState(prev => ({
      ...prev,
      columnOrder: newOrder,
      hiddenColumns: newHidden
    }));
    
    identityServiceRef.current.updateUserSettings({
      [settingsKey]: JSON.stringify({ order: newOrder, hidden: newHidden })
    }).catch(err => {
      console.error('Failed to save settings:', err);
    });
  }, [settingsKey]);

  return {
    columnOrder: state.columnOrder,
    hiddenColumns: state.hiddenColumns,
    isLoading: state.isLoading,
    saveSettings
  };
};
